/**
 * Agent Management API Client
 * Handles all agent and site related API calls
 */

import { AxiosInstance } from 'axios';

import { getApiBaseUrl } from '../config/api';
import type {
  DiscoveredAgent,
  AdoptDiscoveredAgentRequest,
  AgentDiagnostics,
} from '../types/api';
import performanceMonitor from '../utils/performanceMonitor';

import {
  attachAuthRefreshInterceptor,
  createHttpClient,
  normalizeApiError,
} from './http';

// Agent Types
export interface AgentSystemMetrics {
  cpu_percent?: number;
  memory_used_mb?: number;
  memory_total_mb?: number;
  disk_used_gb?: number;
  disk_total_gb?: number;
}

export interface AgentNVRInfo {
  connected?: boolean;
  degraded?: boolean;
  degraded_nvrs?: number;
  active_cameras?: number;
  clips_processing?: number;
  clips_completed?: number;
}

export interface Agent {
  id: string;
  device_id: string;
  tenant_id: string;
  site_id: string;
  name: string;
  ip_address: string;
  status: 'online' | 'offline' | 'error' | 'maintenance';
  version?: string;
  agent_type?: string;
  last_heartbeat?: string;
  heartbeat_received_at?: string;
  build_date?: string;
  clock_skew_ms?: number;
  ntp_offset_ms?: number;
  ntp_server?: string;
  metadata?: Record<string, unknown>;
  config?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  last_seen?: string;
  // Real-time metrics from NATS heartbeat (stored as JSONB in DB)
  system_metrics?: AgentSystemMetrics;
  nvr_info?: AgentNVRInfo;
}

export interface AgentCreateRequest {
  device_id: string;
  tenant_id: string;
  site_id: string;
  name: string;
  ip_address: string;
  status?: string;
  version?: string;
  agent_type?: string;
  metadata?: Record<string, unknown>;
}

export interface AgentUpdateRequest {
  name?: string;
  ip_address?: string;
  status?: string;
  version?: string;
  metadata?: Record<string, unknown>;
  config?: Record<string, unknown>;
}

export interface AgentListResponse {
  agents: Agent[];
  total: number;
  page: number;
  limit: number;
}

export interface AgentListParams {
  tenant_id?: string;
  site_id?: string;
  status?: string;
  page?: number;
  limit?: number;
}

// Tenant Types
export interface Tenant {
  id: string;
  name: string;
  status: string;
  contact_email?: string;
  created_at: string;
  updated_at: string;
}

export interface TenantListResponse {
  tenants: Tenant[];
}

// ZTP Types

/** Lifecycle of a pending_agents row (see main-service modules/agent/model/pending_agent.go). */
export type PendingAgentStatus =
  'pending' | 'approved' | 'claimed' | 'rejected';

export interface PendingAgent {
  fingerprint: string;
  ip_address: string;
  hostname: string;
  hardware_info: Record<string, unknown>;
  status: string;
  created_at: string;
  updated_at?: string;
  last_seen_at?: string;
}

export interface ApprovePendingAgentRequest {
  fingerprint: string;
  tenant_id: string;
  site_id: string;
  name: string;
  agent_type?: string;
  nvr_host?: string;
  s3_endpoint?: string;
}

export interface PendingAgentListResponse {
  data: Array<{
    fingerprint: string;
    ip_address: string;
    hostname: string;
    hardware_info?: Record<string, unknown>;
    status: string;
    created_at: string;
    updated_at?: string;
    last_seen?: string;
    last_seen_at?: string;
  }>;
}

// Site Types
export interface Site {
  id: string;
  name: string;
  tenant_id: string;
  location: string;
  timezone: string;
  status: string;
  description?: string;
  config?: Record<string, unknown>;
  image_url?: string;
  created_at: string;
  updated_at: string;
}

export interface SiteCreateRequest {
  name: string;
  tenant_id: string;
  location: string;
  timezone: string;
  status?: string;
  description?: string;
  config?: Record<string, unknown>;
  image_url?: string;
}

export interface SiteUpdateRequest {
  name?: string;
  tenant_id?: string;
  location?: string;
  timezone?: string;
  status?: string;
  description?: string;
  config?: Record<string, unknown>;
  image_url?: string;
}

export interface SiteListResponse {
  sites: Site[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface CameraZone {
  id: string;
  name: string;
  description?: string;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface CameraZoneListResponse {
  zones: CameraZone[];
  total: number;
}

// WebSocket Log Message
export interface LogMessage {
  agent_id: string;
  device_id: string;
  level: string;
  message: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

// API Error Response
export interface ApiError {
  error: string;
  details?: string;
}

// Enrollment Types
export interface EnrollmentToken {
  id: string;
  token: string;
  agent_type: 'edge' | 'cloud';
  site_id: string;
  site_name?: string;
  expires_at: string;
  created_by: string;
  created_at: string;
  is_active: boolean;
  used_at?: string;
  used_by_device_id?: string;
}

export interface GenerateTokenRequest {
  agent_type: 'edge' | 'cloud';
  site_id: string;
  expires_in_days?: number;
}

export interface GenerateTokenResponse {
  id: string;
  token: string;
  expires_at: string;
}

export interface AgentQuota {
  used: number;
  limit: number;
  remaining: number;
}

export interface TokenListResponse {
  tokens: EnrollmentToken[];
  total: number;
  page: number;
  limit: number;
}

// Metrics Types from Backend
export interface MetricPoint {
  timestamp: string;
  value: number;
}

export interface AgentMetricsResponse {
  agent_id: string;
  metrics: {
    cpu_usage: MetricPoint[];
    memory_usage: MetricPoint[];
    network_in: MetricPoint[];
    network_out: MetricPoint[];
  };
}

// Camera associated with an agent (from /agents/:id/cameras)
export interface AgentCamera {
  id: string;
  name: string;
  type: string;
  ip_address: string;
  status: string;
  agent_id?: string;
}

// Face detection event from /api/v1/face/events
export interface FaceEvent {
  id: string;
  camera_id: string;
  site_id: string;
  agent_id: string;
  person_id?: string;
  person_name?: string;
  person_type: string;
  confidence: number;
  event_type: string;
  snapshot_url?: string;
  bounding_box?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  detected_at: string;
  created_at: string;
}

// Person (face registry) — /api/v1/persons
export interface Person {
  id: string;
  tenant_id: string;
  external_id?: string;
  name: string;
  type: string; // 'member' | 'visitor' | 'staff' | 'vip' | 'blacklist'
  group_id?: string;
  metadata?: Record<string, unknown>;
  consent_given?: boolean;
  consent_given_at?: string;
  created_at?: string;
  updated_at?: string;
}

export interface ScanNetworkRequest {
  scan_type?: string;
  targets?: string[];
  ports?: number[];
  action?: 'start' | 'pause' | 'resume' | 'cancel';
  scan_id?: string;
  username?: string;
  password?: string;
  remember?: boolean;
}

export interface LastScanCredentials {
  username: string;
  password: string;
  has_remembered: boolean;
}

/**
 * Agent API Client
 */
export class AgentApiClient {
  private client: AxiosInstance;
  private genericClient: AxiosInstance;
  private wsConnections: Map<string, WebSocket>;

  constructor(
    baseURL: string = getApiBaseUrl() + '/api/v1',
    timeout: number = 30000
  ) {
    // Shared transport (src/api/http.ts): auth request interceptor with
    // agentClient semantics — always (re)set Authorization from the shared
    // token accessor, no tenant header, no request metadata/logging.
    this.client = createHttpClient({
      baseURL,
      name: 'agent-api',
      timeout,
      overwriteAuthHeader: true,
    });

    this.wsConnections = new Map();

    // 401 → refresh token → retry once. The `_retry` flag prevents infinite
    // loops when the refreshed token is also rejected. Refresh is delegated to
    // refreshAuthToken() which dedupes concurrent 401s via an in-flight
    // Promise, so N parallel failures share ONE Logto SDK refresh call.
    //
    // onAuthFailure: 'logout-redirect' — when the refresh itself cannot
    // recover (e.g. the underlying Logto refresh token has also expired),
    // wipe stored tokens and send the user to /login instead of silently
    // rejecting forever. This used to be 'reject' to avoid a redirect loop
    // with api.js, back when each client had its own ad-hoc redirect logic;
    // both now share the same idempotent redirect guard in http.ts
    // (`here.pathname !== '/login'`), so a stale/expired session surfaces as
    // a login prompt instead of an indefinite silent 401 loop (see the
    // 2026-09-16 401-on-create-site investigation).
    attachAuthRefreshInterceptor(this.client, {
      name: 'agent-api',
      onAuthFailure: 'logout-redirect',
    });

    // Separate axios instance for generic HTTP methods (ApiClient-compatible).
    // Uses bare getApiBaseUrl() so that endpoint paths like /api/v1/agents
    // and root-level paths like /health both resolve correctly.
    this.genericClient = createHttpClient({
      baseURL: getApiBaseUrl(),
      name: 'agent-generic',
      timeout,
      overwriteAuthHeader: true,
    });
    attachAuthRefreshInterceptor(this.genericClient, {
      name: 'agent-generic',
      onAuthFailure: 'logout-redirect',
    });
  }

  // Generic HTTP methods (ApiClient-compatible, axios-backed)

  public async get<T>(
    endpoint: string,
    config?: { params?: object; headers?: Record<string, string> }
  ): Promise<T> {
    try {
      const response = await this.genericClient.get<T>(endpoint, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async post<T>(
    endpoint: string,
    data?: unknown,
    config?: { headers?: Record<string, string> }
  ): Promise<T> {
    try {
      const response = await this.genericClient.post<T>(endpoint, data, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async put<T>(
    endpoint: string,
    data?: unknown,
    config?: { headers?: Record<string, string> }
  ): Promise<T> {
    try {
      const response = await this.genericClient.put<T>(endpoint, data, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async patch<T>(
    endpoint: string,
    data?: unknown,
    config?: { headers?: Record<string, string> }
  ): Promise<T> {
    try {
      const response = await this.genericClient.patch<T>(
        endpoint,
        data,
        config
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  public async delete<T = void>(
    endpoint: string,
    config?: { headers?: Record<string, string> }
  ): Promise<T> {
    try {
      const response = await this.genericClient.delete<T>(endpoint, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Agent CRUD Operations

  /**
   * Create a new agent
   */
  async createAgent(data: AgentCreateRequest): Promise<Agent> {
    const requestId = performanceMonitor.startRequest(
      'create_agent',
      '/agents',
      'POST'
    );
    try {
      // Inject default agent_type if missing, as backend requires it but frontend form doesn't have it.
      const payload = {
        ...data,
        agent_type: data.agent_type || 'video-clipper',
      };
      const response = await this.client.post<Agent>('/agents', payload);
      performanceMonitor.endRequest(requestId, true);
      return response.data;
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Get agent by ID
   */
  async getAgent(id: string): Promise<Agent> {
    const requestId = performanceMonitor.startRequest(
      'get_agent',
      `/agents/${id}`,
      'GET'
    );
    try {
      const response = await this.client.get<Agent>(`/agents/${id}`);
      performanceMonitor.endRequest(requestId, true);
      return response.data;
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * List agents with filters
   */
  async listAgents(params?: AgentListParams): Promise<AgentListResponse> {
    const requestId = performanceMonitor.startRequest(
      'list_agents',
      '/agents',
      'GET'
    );
    try {
      const response = await this.client.get<{
        agents?: Agent[];
        data?: Agent[];
        total?: number;
        limit?: number;
        page_size?: number;
        offset?: number;
        page?: number;
      }>('/agents', {
        params,
      });
      performanceMonitor.endRequest(requestId, true);

      const payload = response.data ?? {};

      // Support both new shape { agents, total, page, limit }
      // and legacy shape { data, total, limit, offset }
      const agents: Agent[] = payload.agents ?? payload.data ?? [];

      const total: number =
        typeof payload.total === 'number'
          ? payload.total
          : (agents.length ?? 0);

      const limit: number =
        typeof payload.limit === 'number'
          ? payload.limit
          : typeof payload.page_size === 'number'
            ? payload.page_size
            : (params?.limit ?? (agents.length || 10));

      const offset: number =
        typeof payload.offset === 'number' ? payload.offset : 0;

      const page: number =
        typeof payload.page === 'number' && payload.page > 0
          ? payload.page
          : limit > 0
            ? Math.floor(offset / limit) + 1
            : 1;

      return { agents, total, page, limit };
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Update agent
   */
  async updateAgent(id: string, data: AgentUpdateRequest): Promise<Agent> {
    const requestId = performanceMonitor.startRequest(
      'update_agent',
      `/agents/${id}`,
      'PUT'
    );
    try {
      const response = await this.client.put<Agent>(`/agents/${id}`, data);
      performanceMonitor.endRequest(requestId, true);
      return response.data;
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Get agent runtime configuration
   */
  async getAgentConfig(id: string): Promise<unknown> {
    const requestId = performanceMonitor.startRequest(
      'get_agent_config',
      `/agents/${id}/config`,
      'GET'
    );
    try {
      const response = await this.client.get<unknown>(`/agents/${id}/config`);
      performanceMonitor.endRequest(requestId, true);
      return response.data;
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Update agent runtime configuration
   */
  async updateAgentConfig(id: string, config: unknown): Promise<unknown> {
    const requestId = performanceMonitor.startRequest(
      'update_agent_config',
      `/agents/${id}/config`,
      'PUT'
    );
    try {
      const response = await this.client.put<unknown>(
        `/agents/${id}/config`,
        config
      );
      performanceMonitor.endRequest(requestId, true);
      return response.data;
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Delete agent
   */
  async deleteAgent(id: string): Promise<void> {
    const requestId = performanceMonitor.startRequest(
      'delete_agent',
      `/agents/${id}`,
      'DELETE'
    );
    try {
      await this.client.delete(`/agents/${id}`);
      performanceMonitor.endRequest(requestId, true);
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Delete all mock agents (dev mode only)
   */
  async deleteMockDevices(): Promise<{ message: string; count: number }> {
    const requestId = performanceMonitor.startRequest(
      'delete_mock_devices',
      `/agents`,
      'DELETE'
    );
    try {
      const response = await this.client.delete<{
        message: string;
        count: number;
      }>(`/agents`);
      performanceMonitor.endRequest(requestId, true);
      return response.data;
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Get Agent Metrics
   */
  async getMetrics(
    id: string,
    hours: number = 1
  ): Promise<AgentMetricsResponse> {
    const requestId = performanceMonitor.startRequest(
      'get_metrics',
      `/agents/${id}/metrics`,
      'GET'
    );
    try {
      const response = await this.client.get<AgentMetricsResponse>(
        `/agents/${id}/metrics`,
        {
          params: { hours },
        }
      );
      performanceMonitor.endRequest(requestId, true);
      return response.data;
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Get cameras associated with an agent
   */
  async getAgentCameras(
    agentId: string
  ): Promise<{ cameras: AgentCamera[]; total: number }> {
    const requestId = performanceMonitor.startRequest(
      'get_agent_cameras',
      `/agents/${agentId}/cameras`,
      'GET'
    );
    try {
      const response = await this.client.get<{
        cameras: AgentCamera[];
        total: number;
      }>(`/agents/${agentId}/cameras`);
      performanceMonitor.endRequest(requestId, true);
      return response.data;
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Get historical face detection events, optionally filtered by agent
   */
  async getFaceEvents(params: {
    agentId?: string;
    cameraId?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    events: FaceEvent[];
    total: number;
    limit: number;
    offset: number;
  }> {
    const requestId = performanceMonitor.startRequest(
      'get_face_events',
      '/face/events',
      'GET'
    );
    try {
      const response = await this.client.get<{
        events: FaceEvent[];
        total: number;
        limit: number;
        offset: number;
      }>('/face/events', {
        params: {
          agent_id: params.agentId,
          camera_id: params.cameraId,
          limit: params.limit ?? 50,
          offset: params.offset ?? 0,
        },
      });
      performanceMonitor.endRequest(requestId, true);
      return response.data;
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * List persons from the face registry
   */
  async listPersons(
    params: { limit?: number; offset?: number } = {}
  ): Promise<Person[]> {
    const requestId = performanceMonitor.startRequest(
      'list_persons',
      '/persons',
      'GET'
    );
    try {
      const response = await this.client.get<Person[]>('/persons', {
        params: { limit: params.limit ?? 100, offset: params.offset ?? 0 },
      });
      performanceMonitor.endRequest(requestId, true);
      return response.data;
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Create a new person in the face registry
   */
  async createPerson(req: {
    name: string;
    type?: string;
    external_id?: string;
    group_id?: string;
    metadata?: Record<string, unknown>;
    consent_given?: boolean;
  }): Promise<Person> {
    const requestId = performanceMonitor.startRequest(
      'create_person',
      '/persons',
      'POST'
    );
    try {
      const response = await this.client.post<Person>('/persons', req);
      performanceMonitor.endRequest(requestId, true);
      return response.data;
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Enroll a face image for a person — used to link a face event to a
   * person record so future matching identifies them.
   */
  async enrollPersonFace(
    personId: string,
    req: { image_base64: string; image_format?: string }
  ): Promise<{ id: string; person_id: string }> {
    const requestId = performanceMonitor.startRequest(
      'enroll_person_face',
      `/persons/${personId}/faces`,
      'POST'
    );
    try {
      const response = await this.client.post<{
        id: string;
        person_id: string;
      }>(`/persons/${personId}/faces`, req);
      performanceMonitor.endRequest(requestId, true);
      return response.data;
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * List pending agents (ZTP). Without `status` the server returns rows waiting for approval;
   * pass 'rejected' for devices an admin turned down (kept server-side so the device stops
   * asking; they can still be approved later or purged).
   */
  async listPendingAgents(
    status?: PendingAgentStatus
  ): Promise<PendingAgent[]> {
    const url =
      status && status !== 'pending'
        ? `/agents/pending?status=${encodeURIComponent(status)}`
        : '/agents/pending';
    const requestId = performanceMonitor.startRequest(
      'list_pending_agents',
      url,
      'GET'
    );
    try {
      const response = await this.client.get<PendingAgentListResponse>(url);
      performanceMonitor.endRequest(requestId, true);

      const agents = response.data.data || [];
      return agents.map(a => ({
        fingerprint: a.fingerprint,
        hostname: a.hostname,
        ip_address: a.ip_address,
        hardware_info: a.hardware_info || {},
        status: a.status,
        created_at: a.created_at,
        updated_at: a.updated_at,
        last_seen_at: a.last_seen_at || a.last_seen,
      }));
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Approve pending agent (ZTP)
   */
  async approvePendingAgent(data: ApprovePendingAgentRequest): Promise<void> {
    const requestId = performanceMonitor.startRequest(
      'approve_pending_agent',
      '/agents/approve',
      'POST'
    );
    try {
      await this.client.post('/agents/approve', {
        fingerprint: data.fingerprint,
        name: data.name,
        tenant_id: data.tenant_id,
        site_id: data.site_id,
        agent_type: data.agent_type || 'edge-agent',
        nvr_host: data.nvr_host,
        s3_endpoint: data.s3_endpoint,
      });
      performanceMonitor.endRequest(requestId, true);
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Reject pending agent (ZTP). The row is kept with status "rejected" so the device's status
   * poll gets a definitive answer instead of a 404 (which made it re-register within 30s).
   */
  async rejectPendingAgent(fingerprint: string): Promise<void> {
    const requestId = performanceMonitor.startRequest(
      'reject_pending_agent',
      `/agents/pending/${fingerprint}`,
      'DELETE'
    );
    try {
      await this.client.delete(`/agents/pending/${fingerprint}`);
      performanceMonitor.endRequest(requestId, true);
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Permanently delete a pending/rejected row (ZTP). Also the escape hatch for a device that
   * lost its ztp_key.pem: its re-registration is refused (409 key_mismatch) until the stale
   * binding is purged.
   */
  async purgePendingAgent(fingerprint: string): Promise<void> {
    const url = `/agents/pending/${fingerprint}?purge=true`;
    const requestId = performanceMonitor.startRequest(
      'purge_pending_agent',
      url,
      'DELETE'
    );
    try {
      await this.client.delete(url);
      performanceMonitor.endRequest(requestId, true);
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Update agent heartbeat
   */
  async updateHeartbeat(id: string): Promise<void> {
    const requestId = performanceMonitor.startRequest(
      'update_heartbeat',
      `/agents/${id}/heartbeat`,
      'POST'
    );
    try {
      await this.client.post(`/agents/${id}/heartbeat`);
      performanceMonitor.endRequest(requestId, true);
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Restart agent
   */
  async restartAgent(id: string, reason?: string): Promise<void> {
    const requestId = performanceMonitor.startRequest(
      'restart_agent',
      `/agents/${id}/restart`,
      'POST'
    );
    try {
      await this.client.post(`/agents/${id}/restart`, {
        // Backend expects a non-empty reason field; provide a sensible default
        reason: reason || 'Manual restart from admin portal',
      });
      performanceMonitor.endRequest(requestId, true);
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Force an agent to drop its identity and re-register from scratch.
   * Use when the agent is stuck in SAFE MODE or its identity is no longer recognized.
   */
  async forceReregisterAgent(id: string, reason?: string): Promise<void> {
    const requestId = performanceMonitor.startRequest(
      'force_reregister_agent',
      `/agents/${id}/force-reregister`,
      'POST'
    );
    try {
      const body: Record<string, string> = {};
      if (reason && reason.trim()) {
        body.reason = reason.trim();
      }
      await this.client.post(`/agents/${id}/force-reregister`, body);
      performanceMonitor.endRequest(requestId, true);
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Trigger agent network scan
   */
  async scanNetwork(id: string, request?: ScanNetworkRequest): Promise<void> {
    const requestId = performanceMonitor.startRequest(
      'scan_network',
      `/agents/${id}/scan`,
      'POST'
    );
    try {
      await this.client.post(`/agents/${id}/scan`, request);
      performanceMonitor.endRequest(requestId, true);
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Sweep the configured LAN subnets for edge agents that are reachable but
   * not yet registered with main-service. Returns the list of discovered
   * agents (fingerprint, IP, version, host, etc.).
   */
  async discoverAgents(subnet?: string): Promise<DiscoveredAgent[]> {
    const requestId = performanceMonitor.startRequest(
      'discover_agents',
      '/admin/discover/agents',
      'POST'
    );
    try {
      const { data } = await this.client.post<{ agents: DiscoveredAgent[] }>(
        '/admin/discover/agents',
        subnet ? { subnet } : {}
      );
      performanceMonitor.endRequest(requestId, true);
      return data.agents ?? [];
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Adopt a discovered agent: creates an `agents` row with status=adopted so
   * the operator can name, site, and approve the device.
   */
  async adoptDiscoveredAgent(
    fingerprint: string,
    payload: AdoptDiscoveredAgentRequest
  ): Promise<{ agent_id: string }> {
    const requestId = performanceMonitor.startRequest(
      'adopt_discovered_agent',
      `/admin/discover/agents/${fingerprint}/adopt`,
      'POST'
    );
    try {
      const { data } = await this.client.post<{ agent_id: string }>(
        `/admin/discover/agents/${fingerprint}/adopt`,
        payload
      );
      performanceMonitor.endRequest(requestId, true);
      return data;
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Pull a snapshot of why an agent is offline: probe port 8081, capture
   * staleness + last heartbeat, and surface a one-line suggested action.
   * Read-only — no NATS commands are dispatched.
   */
  async diagnoseAgent(id: string): Promise<AgentDiagnostics> {
    const requestId = performanceMonitor.startRequest(
      'diagnose_agent',
      `/agents/${id}/diagnose`,
      'GET'
    );
    try {
      const { data } = await this.client.get<AgentDiagnostics>(
        `/agents/${id}/diagnose`
      );
      performanceMonitor.endRequest(requestId, true);
      return data;
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Dev-mode bulk delete (clears seed/mock agents). The endpoint is wired
   * only when the main-service runs in dev; production calls return 404.
   */
  async deleteMockAgents(): Promise<{
    count: number;
    deleted_names: string[];
  }> {
    const requestId = performanceMonitor.startRequest(
      'delete_mock_agents',
      '/agents',
      'DELETE'
    );
    try {
      const { data } = await this.client.delete<{
        count: number;
        deleted_names: string[];
      }>('/agents');
      performanceMonitor.endRequest(requestId, true);
      return data;
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  // Tenant Operations

  /**
   * List tenants
   */
  async listTenants(): Promise<Tenant[]> {
    const requestId = performanceMonitor.startRequest(
      'list_tenants',
      '/admin/tenants',
      'GET'
    );
    try {
      const response = await this.client.get<{
        tenants: Tenant[];
        total: number;
      }>('/admin/tenants');
      performanceMonitor.endRequest(requestId, true);
      return response.data?.tenants ?? [];
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Get tenant by ID
   */
  async getTenant(id: string): Promise<Tenant> {
    const requestId = performanceMonitor.startRequest(
      'get_tenant',
      `/admin/tenants/${id}`,
      'GET'
    );
    try {
      const response = await this.client.get<Tenant>(`/admin/tenants/${id}`);
      performanceMonitor.endRequest(requestId, true);
      return response.data;
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Create a new tenant
   */
  async createTenant(data: { name: string; status?: string }): Promise<Tenant> {
    const requestId = performanceMonitor.startRequest(
      'create_tenant',
      '/admin/tenants',
      'POST'
    );
    try {
      const response = await this.client.post<Tenant>('/admin/tenants', data);
      performanceMonitor.endRequest(requestId, true);
      return response.data;
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Update tenant
   */
  async updateTenant(
    id: string,
    data: { name?: string; status?: string }
  ): Promise<Tenant> {
    const requestId = performanceMonitor.startRequest(
      'update_tenant',
      `/admin/tenants/${id}`,
      'PUT'
    );
    try {
      const response = await this.client.put<Tenant>(
        `/admin/tenants/${id}`,
        data
      );
      performanceMonitor.endRequest(requestId, true);
      return response.data;
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Delete tenant
   */
  async deleteTenant(id: string): Promise<void> {
    const requestId = performanceMonitor.startRequest(
      'delete_tenant',
      `/admin/tenants/${id}`,
      'DELETE'
    );
    try {
      await this.client.delete(`/admin/tenants/${id}`);
      performanceMonitor.endRequest(requestId, true);
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  // Site CRUD Operations

  /**
   * Create a new site
   */
  async createSite(data: SiteCreateRequest): Promise<Site> {
    const requestId = performanceMonitor.startRequest(
      'create_site',
      '/sites',
      'POST'
    );
    try {
      const response = await this.client.post<Site>('/sites', data);
      performanceMonitor.endRequest(requestId, true);
      return response.data;
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Get site by ID
   */
  async getSite(id: string): Promise<Site> {
    const requestId = performanceMonitor.startRequest(
      'get_site',
      `/sites/${id}`,
      'GET'
    );
    try {
      const response = await this.client.get<Site>(`/sites/${id}`);
      performanceMonitor.endRequest(requestId, true);
      return response.data;
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * List sites for tenant
   */
  async listSites(tenantId: string): Promise<SiteListResponse> {
    const requestId = performanceMonitor.startRequest(
      'list_sites',
      '/sites',
      'GET'
    );
    try {
      const response = await this.client.get<SiteListResponse>('/sites', {
        params: { tenant_id: tenantId },
      });
      performanceMonitor.endRequest(requestId, true);
      return response.data;
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Update site
   */
  async updateSite(id: string, data: SiteUpdateRequest): Promise<Site> {
    const requestId = performanceMonitor.startRequest(
      'update_site',
      `/sites/${id}`,
      'PUT'
    );
    try {
      const response = await this.client.put<Site>(`/sites/${id}`, data);
      performanceMonitor.endRequest(requestId, true);
      return response.data;
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Delete site
   */
  async deleteSite(id: string): Promise<void> {
    const requestId = performanceMonitor.startRequest(
      'delete_site',
      `/sites/${id}`,
      'DELETE'
    );
    try {
      await this.client.delete(`/sites/${id}`);
      performanceMonitor.endRequest(requestId, true);
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * List camera zones (admin scope)
   */
  async listCameraZones(siteId: string): Promise<CameraZoneListResponse> {
    const requestId = performanceMonitor.startRequest(
      'list_camera_zones',
      `/admin/camera-zones?site_id=${siteId}`,
      'GET'
    );
    try {
      const response = await this.client.get<CameraZoneListResponse>(
        `/admin/camera-zones?site_id=${siteId}`
      );
      performanceMonitor.endRequest(requestId, true);
      return response.data;
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Create camera zone (admin scope)
   */
  async createCameraZone(data: {
    name: string;
    site_id: string;
    description?: string;
  }): Promise<CameraZone> {
    const requestId = performanceMonitor.startRequest(
      'create_camera_zone',
      '/admin/camera-zones',
      'POST'
    );
    try {
      const response = await this.client.post<CameraZone>(
        '/admin/camera-zones',
        data
      );
      performanceMonitor.endRequest(requestId, true);
      return response.data;
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Update camera zone (admin scope)
   */
  async updateCameraZone(
    id: string,
    data: { name?: string; description?: string; is_active?: boolean }
  ): Promise<CameraZone> {
    const requestId = performanceMonitor.startRequest(
      'update_camera_zone',
      `/admin/camera-zones/${id}`,
      'PUT'
    );
    try {
      const response = await this.client.put<CameraZone>(
        `/admin/camera-zones/${id}`,
        data
      );
      performanceMonitor.endRequest(requestId, true);
      return response.data;
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Delete camera zone (admin scope)
   */
  async deleteCameraZone(id: string): Promise<void> {
    const requestId = performanceMonitor.startRequest(
      'delete_camera_zone',
      `/admin/camera-zones/${id}`,
      'DELETE'
    );
    try {
      await this.client.delete(`/admin/camera-zones/${id}`);
      performanceMonitor.endRequest(requestId, true);
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  // Enrollment Operations

  /**
   * Generate Enrollment Token
   */
  async generateToken(
    data: GenerateTokenRequest
  ): Promise<GenerateTokenResponse> {
    const requestId = performanceMonitor.startRequest(
      'generate_token',
      '/enrollment/tokens',
      'POST'
    );
    try {
      const response = await this.client.post<GenerateTokenResponse>(
        '/enrollment/tokens',
        data
      );
      performanceMonitor.endRequest(requestId, true);
      return response.data;
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * List Enrollment Tokens
   */
  async listTokens(params?: {
    page?: number;
    limit?: number;
  }): Promise<TokenListResponse> {
    const requestId = performanceMonitor.startRequest(
      'list_tokens',
      '/enrollment/tokens',
      'GET'
    );
    try {
      const response = await this.client.get<TokenListResponse>(
        '/enrollment/tokens',
        { params }
      );
      performanceMonitor.endRequest(requestId, true);
      return response.data;
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Revoke Enrollment Token
   */
  async revokeToken(id: string): Promise<void> {
    const requestId = performanceMonitor.startRequest(
      'revoke_token',
      `/enrollment/tokens/${id}`,
      'DELETE'
    );
    try {
      await this.client.delete(`/enrollment/tokens/${id}`);
      performanceMonitor.endRequest(requestId, true);
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  // WebSocket Operations

  /**
   * Connect to agent log stream
   */
  connectToLogStream(
    agentId: string,
    onMessage: (log: LogMessage) => void,
    onError?: (error: Event) => void,
    onClose?: (event: CloseEvent) => void
  ): void {
    // Close existing connection if any
    this.disconnectFromLogStream(agentId);

    const wsUrl = `${this.getWebSocketURL()}/agents/${agentId}/logs/stream`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {};

    ws.onmessage = event => {
      // Backend sends newline-delimited JSON or single JSON
      // We must handle multiple messages in one frame
      try {
        const rawData = typeof event.data === 'string' ? event.data : '';
        if (!rawData) return;

        const messages = rawData.split('\n');

        for (const msg of messages) {
          if (!msg.trim()) continue; // Skip empty lines

          try {
            const log: LogMessage = JSON.parse(msg);
            onMessage(log);
          } catch (innerError) {
            console.error(
              'Failed to parse individual log line:',
              innerError,
              msg
            );
          }
        }
      } catch (error) {
        console.error('Failed to process WebSocket message:', error);
      }
    };

    ws.onerror = error => {
      console.error(`WebSocket error for agent ${agentId}:`, error);
      if (onError) onError(error);
    };

    ws.onclose = event => {
      this.wsConnections.delete(agentId);
      if (onClose) onClose(event);
    };

    this.wsConnections.set(agentId, ws);
  }

  /**
   * Disconnect from agent log stream
   */
  disconnectFromLogStream(agentId: string): void {
    const ws = this.wsConnections.get(agentId);
    if (ws) {
      ws.close();
      this.wsConnections.delete(agentId);
    }
  }

  /**
   * Returns true if a log stream WebSocket exists and is open for the given agentId.
   * Used by the store to decide whether to skip a scheduled reconnect.
   */
  isLogStreamActive(agentId: string): boolean {
    const ws = this.wsConnections.get(agentId);
    return !!ws && ws.readyState === WebSocket.OPEN;
  }

  /**
   * Disconnect all WebSocket connections
   */
  disconnectAll(): void {
    this.wsConnections.forEach(ws => {
      ws.close();
    });
    this.wsConnections.clear();
  }

  async getScanStatus(agentId: string): Promise<{
    active: boolean;
    scans: Array<{ scan_id: string; status: string; created_at: string }>;
  }> {
    const response = await this.client.get(`/agents/${agentId}/scan/status`);
    return response.data;
  }

  async getLastScanCredentials(agentId: string): Promise<LastScanCredentials> {
    const response = await this.client.get(
      `/agents/${agentId}/last-scan-credentials`
    );
    return response.data;
  }

  async cancelScan(agentId: string, scanId?: string): Promise<void> {
    await this.client.post(
      `/agents/${agentId}/scan/cancel`,
      scanId ? { scan_id: scanId } : undefined
    );
  }

  /**
   * Publish log message (for testing)
   */
  async publishLog(
    agentId: string,
    log: Omit<LogMessage, 'agent_id'>
  ): Promise<void> {
    const requestId = performanceMonitor.startRequest(
      'publish_log',
      `/agents/${agentId}/logs`,
      'POST'
    );
    try {
      await this.client.post(`/agents/${agentId}/logs`, log);
      performanceMonitor.endRequest(requestId, true);
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  // Helper Methods

  private getWebSocketURL(): string {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    return `${protocol}//${host}/api/v1`;
  }

  private handleError(error: unknown): Error {
    // Shared normalization (src/api/http.ts) — output format is identical
    // to the previous inline implementation.
    return normalizeApiError(error);
  }
  /**
   * Link a face event to an existing person. Sets person_id / person_name
   * / person_type on the face_detection_events row so the event shows as
   * identified on the list page.
   */
  async linkFaceEventToPerson(
    eventId: string,
    req: {
      person_id: string;
    }
  ): Promise<{
    event_id: string;
    person_id: string;
    person_name: string;
    person_type: string;
  }> {
    const requestId = performanceMonitor.startRequest(
      'link_face_event_to_person',
      `/face/events/${eventId}/person`,
      'PUT'
    );
    try {
      const response = await this.client.put<{
        event_id: string;
        person_id: string;
        person_name: string;
        person_type: string;
      }>(`/face/events/${eventId}/person`, req);
      performanceMonitor.endRequest(requestId, true);
      return response.data;
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }

  /**
   * Unlink a face event from its assigned person. Resets person_type to
   * 'anonymous' on the face_detection_events row.
   */
  async unlinkFaceEventFromPerson(
    eventId: string
  ): Promise<{ event_id: string }> {
    const requestId = performanceMonitor.startRequest(
      'unlink_face_event_from_person',
      `/face/events/${eventId}/person`,
      'DELETE'
    );
    try {
      const response = await this.client.delete<{ event_id: string }>(
        `/face/events/${eventId}/person`
      );
      performanceMonitor.endRequest(requestId, true);
      return response.data;
    } catch (error) {
      performanceMonitor.endRequest(requestId, false, error as Error);
      throw this.handleError(error);
    }
  }
}

// Export singleton instance
export const agentApiClient = new AgentApiClient();
export default agentApiClient;
